import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { CallOutcome, CallSession } from "./types";

interface SessionSummaryProps {
  session: CallSession;
  onClose: () => void;
}

/** End-of-session screen — outcome counts + per-call log. The data
 *  is entirely in-memory (CallSession.results), so this renders with
 *  no extra fetches even if the analytics endpoints are offline. */
export function SessionSummary({ session, onClose }: SessionSummaryProps) {
  const { results } = session;
  const called = results.filter(
    (r) => !["skipped", "refused"].includes(r.outcome),
  );
  const counts = countBy(results, (r) => r.outcome);
  const interested = counts.interested ?? 0;
  const neutral = counts.neutral ?? 0;
  const notInterested = counts.not_interested ?? 0;
  const noAnswer = counts.no_answer ?? 0;
  const refused = counts.refused ?? 0;
  const skipped = counts.skipped ?? 0;
  const totalTalkSec = results.reduce(
    (sum, r) => sum + (r.durationSeconds ?? 0),
    0,
  );
  const reachRate =
    called.length > 0
      ? Math.round(((called.length - noAnswer) / called.length) * 100)
      : 0;
  const elapsedMin = Math.max(
    1,
    Math.round((Date.now() - session.startedAtMs) / 60_000),
  );

  return (
    <div className="mx-auto max-w-2xl space-y-8 py-12 text-center">
      <div>
        <div className="mb-4 text-6xl">🎉</div>
        <h1 className="text-2xl font-bold text-slate-900">Session Complete!</h1>
        <p className="mt-1 text-slate-500">
          {called.length} calls in {elapsedMin} minutes
        </p>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <SummaryCard
          tone="emerald"
          value={interested}
          label="Interested"
        />
        <SummaryCard tone="amber" value={neutral} label="Neutral" />
        <SummaryCard tone="slate" value={noAnswer} label="No Answer" />
        <SummaryCard
          tone="rose"
          value={notInterested + refused}
          label="Refused"
        />
      </div>

      <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-500">
        <span>
          Reach rate:{" "}
          <strong className="text-slate-800">{reachRate}%</strong>
        </span>
        <span>
          Talk time:{" "}
          <strong className="text-slate-800">
            {Math.round(totalTalkSec / 60)} min
          </strong>
        </span>
        <span>
          Skipped: <strong className="text-slate-800">{skipped}</strong>
        </span>
      </div>

      <div className="rounded-xl border border-slate-900/[0.06] p-4 text-left">
        <h3 className="mb-3 text-sm font-medium text-slate-700">Call log</h3>
        <div className="max-h-[300px] space-y-1 overflow-y-auto">
          {results.map((r, i) => (
            <div
              key={i}
              className="flex items-center justify-between border-b border-slate-50 py-1.5 text-sm last:border-0"
            >
              <span className="truncate text-slate-700">{r.companyName}</span>
              <div className="flex items-center gap-3">
                {r.durationSeconds != null && (
                  <span className="text-xs text-slate-400">
                    {Math.floor(r.durationSeconds / 60)}:
                    {String(r.durationSeconds % 60).padStart(2, "0")}
                  </span>
                )}
                <OutcomePill outcome={r.outcome} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={onClose} size="lg">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Call Center
      </Button>
    </div>
  );
}

const SUMMARY_TONES: Record<string, string> = {
  emerald: "border-emerald-100 bg-emerald-50 text-emerald-600",
  amber: "border-amber-100 bg-amber-50 text-amber-600",
  slate: "border-slate-900/[0.06] bg-slate-50 text-slate-600",
  rose: "border-rose-100 bg-rose-50 text-rose-600",
};
const SUMMARY_LABEL_TONES: Record<string, string> = {
  emerald: "text-emerald-500",
  amber: "text-amber-500",
  slate: "text-slate-500",
  rose: "text-rose-500",
};

function SummaryCard({
  tone,
  value,
  label,
}: {
  tone: "emerald" | "amber" | "slate" | "rose";
  value: number;
  label: string;
}) {
  return (
    <div className={cn("rounded-xl border p-4", SUMMARY_TONES[tone])}>
      <div className="text-3xl font-bold">{value}</div>
      <div className={cn("mt-1 text-xs", SUMMARY_LABEL_TONES[tone])}>
        {label}
      </div>
    </div>
  );
}

const OUTCOME_PILL: Record<CallOutcome, string> = {
  interested: "bg-emerald-50 text-emerald-700",
  neutral: "bg-amber-50 text-amber-700",
  no_answer: "bg-slate-100 text-slate-600",
  skipped: "bg-blue-50 text-blue-600",
  refused: "bg-rose-50 text-rose-700",
  not_interested: "bg-rose-50 text-rose-700",
};

function OutcomePill({ outcome }: { outcome: CallOutcome }) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-xs font-medium",
        OUTCOME_PILL[outcome],
      )}
    >
      {outcome.replace(/_/g, " ")}
    </span>
  );
}

function countBy<T>(items: T[], key: (t: T) => string): Record<string, number> {
  const out: Record<string, number> = {};
  for (const item of items) {
    const k = key(item);
    out[k] = (out[k] ?? 0) + 1;
  }
  return out;
}
